import React, { FC, useMemo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { ActionsButton, Datatable } from 'ui';
import { useSelectedTableRows, useTable } from 'shared/hooks';
import { MUIDataTableMeta } from 'mui-datatables';
import { useDeleteUpload, useFetchPortfolioUploads } from '../../GraphQL/portfolios/hooks';
import { makeUploadsTableData } from '../utils';
import { PortfolioItemsProps, PortfolioUpload } from '../types';
import { PortfolioUploadModal } from '../../Modals/PortfolioUpload/PortfolioUploadModal';
import { theme } from '../../../../theme';
import { tableRefreshReasonTypes as refreshReasons } from '../../../../shared/constants/tableRefreshReasonTypes';
import { useSnackbar } from '../../../../shared/hooks/useSnackbar';
import { UploadStatus } from './components/UploadStatus';

const useStyles = makeStyles(() => ({
  root: {
    '& thead > tr': {
      whiteSpace: 'pre',
    },
  },
  buttonLink: {
    cursor: 'pointer',
  },
}));

export const PortfolioUploads: FC<PortfolioItemsProps> = ({ portfolioId, total = 0 }) => {
  const { t } = useTranslation();
  const [updating, setUpdating] = useState(false);
  const classes = useStyles();
  const { error, success } = useSnackbar();
  const deleteUpload = useDeleteUpload();
  const { selectSingle, onSelectedChange } = useSelectedTableRows();
  const [shouldRefresh, setShouldRefresh] = useState({ value: false, reason: '' });
  const [portfolioUploadModalOpen, setPortfolioUploadModalOpen] = useState(false);

  const closeUploadPortfolioModal = () => {
    setPortfolioUploadModalOpen(false);
    refetch();
  };

  /* ******************************************************************** */
  /*                            Event handlers                            */
  /* ******************************************************************** */

  const deleteFile = (id) => {
    setUpdating(true);
    deleteUpload({
      uploadsIds: id,
      portfolioId,
    })
      .then(({ data }) =>
        refreshList(true, refreshReasons.DELETED).finally(() =>
          success(data?.deleteInstruments?.message || t('Success')),
        ),
      )
      .catch(() => error(t('Unable to delete upload.')))
      .finally(() => setUpdating(false));
  };

  const uploadPortfolio = () => {
    setPortfolioUploadModalOpen(true);
  };

  const downloadFile = (val) => window.open(val);

  /* ******************************************************************** */
  /*                         Custom body renders                          */
  /* ******************************************************************** */

  // eslint-disable-next-line no-unused-vars
  const nameRenderer = ([name, _link]: string[]) => {
    return (
      <Button component="a" href={_link} target="_blank">
        {name}
      </Button>
    );
  };

  const typeRender = (name) => (
    <Typography>{name === 'BONDS' ? 'ENTITIES/BONDS' : name}</Typography>
  );

  const statusRenderer = (item: PortfolioUpload) => (
    <UploadStatus
      item={item}
      onDownloadDetails={() => downloadFile(item.link)}
      onDownloadErrorReport={() => downloadFile(item.errorsLink)}
    />
  );

  const uploadedItemsRender = (val: string) => val ?? '-';

  const validationErrorsRender = (val: string) =>
    val ? (
      <Typography color={theme.palette.custom.error}>{val}</Typography>
    ) : (
      <Typography>-</Typography>
    );

  const actionRender = (item, { rowData: [[id]] }: MUIDataTableMeta) => (
    <ActionsButton onOpen={() => selectSingle(id)} actions={getActions(item)} />
  );

  /* ******************************************************************** */
  /*                       Table columns definition                       */
  /* ******************************************************************** */

  const initialColumns = useCallback(
    () => [
      {
        name: 'id',
        options: {
          display: false,
        },
      },
      {
        name: 'name',
        label: t('PORTFOLIO.UPLOADS_NAME'),
        options: {
          customBodyRender: nameRenderer,
        },
      },
      {
        name: 'type',
        label: t('PORTFOLIO.UPLOADS_TYPE'),
        options: {
          customBodyRender: typeRender,
        },
      },
      {
        name: 'uploaded',
        label: t('PORTFOLIO.UPLOADED_ITEMS'),
        options: {
          customBodyRender: uploadedItemsRender,
        },
      },
      {
        name: 'validation',
        label: t('PORTFOLIO.VALIDATION_ERRORS'),
        options: {
          customBodyRender: validationErrorsRender,
        },
      },
      {
        name: 'status',
        label: t('PORTFOLIO.UPLOADS_STATUS'),
        options: {
          customBodyRender: statusRenderer,
        },
      },
      {
        name: 'moreActions',
        label: ' ',
        options: {
          sort: false,
          customBodyRender: actionRender,
        },
      },
    ],
    [t],
  );

  const initialSort = {
    sortColumn: 'created',
    direction: 'DESC',
  };

  /* ******************************************************************** */
  /*                             More actions                             */
  /* ******************************************************************** */

  const getActions = (item: PortfolioUpload) => {
    return [
      {
        text: 'Download file',
        handler: () => downloadFile(item.link),
        disabled: !item.link,
        hidden: false,
      },
      {
        text: 'Validation report',
        handler: () => downloadFile(item.errorsLink),
        disabled: !item.errorsLink,
      },
      {
        text: 'Delete file',
        handler: () => deleteFile(item.id),
        color: theme.palette.error.main,
        hidden: false,
      },
    ];
  };

  const getUploadAction = () => [{ text: 'Upload File', handler: uploadPortfolio, hidden: false }];

  /* ******************************************************************** */
  /*                              Table data                              */
  /* ******************************************************************** */
  const { columns, pagination, onSortChange, onTableChange } = useTable({
    initialColumns,
    initialSort,
  } as any);

  const { data, loading, refetch } = useFetchPortfolioUploads(
    portfolioId,
    pagination.page * pagination.limit,
    pagination.limit,
    pagination.sort && [
      {
        column: pagination.sort.sortColumn,
        direction: pagination.sort.direction,
      },
    ],
  );
  const { uploadHistory } = data || { uploadHistory: [] };

  const tableData = useMemo(() => makeUploadsTableData(uploadHistory), [uploadHistory]);

  const refreshList = async (refresh: boolean, reason: string) => {
    setUpdating(true);
    await refetch();
    setShouldRefresh({ value: refresh, reason });
    setUpdating(false);
  };

  /* ******************************************************************** */
  /*                                Render                                */
  /* ******************************************************************** */
  return (
    <Box className={classes.root}>
      <PortfolioUploadModal
        open={portfolioUploadModalOpen}
        onClose={closeUploadPortfolioModal}
        destinationPortfolioId={portfolioId}
      />
      <Datatable
        loading={loading || updating}
        columns={columns}
        pagination={pagination}
        data={tableData}
        total={total}
        toolbarData={getUploadAction()}
        shouldRefresh={shouldRefresh}
        onTableChange={onTableChange}
        onSortChange={onSortChange}
        removeShadow
        selectableRows={false}
        onSelectedChange={onSelectedChange}
      />
    </Box>
  );
};
